import React from 'react';
import { Badge } from '@/components/ui/badge';
import { UserRole } from '@/types';
import { 
  Shield, 
  Wrench, 
  Home, 
  Building, 
  Briefcase,
  UserCog,
  Users
} from 'lucide-react';

interface SidebarRoleIndicatorProps {
  role: UserRole;
  collapsed?: boolean;
}

const roleConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  admin: { label: 'Administrator', icon: UserCog, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  manager: { label: 'Manager', icon: Briefcase, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  maintenance_tech: { label: 'Technician', icon: Wrench, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  maintenance_supervisor: { label: 'Supervisor', icon: Users, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  maintenance_coordinator: { label: 'Coordinator', icon: Users, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  maintenance_director: { label: 'Director', icon: Briefcase, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  vendor: { label: 'Vendor', icon: Building, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  vendor_tech: { label: 'Vendor Tech', icon: Wrench, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  vendor_admin: { label: 'Vendor Admin', icon: UserCog, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  vendor_manager: { label: 'Vendor Mgr', icon: Users, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  tenant: { label: 'Resident', icon: Home, color: 'bg-sky-500/10 text-sky-600 border-sky-500/20' },
  resident: { label: 'Resident', icon: Home, color: 'bg-sky-500/10 text-sky-600 border-sky-500/20' },
  insurance_adjuster: { label: 'Insurance', icon: Shield, color: 'bg-red-500/10 text-red-600 border-red-500/20' },
  lender: { label: 'Lender', icon: Building, color: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
  vendorshield_company_admin: { label: 'VL Admin', icon: Shield, color: 'bg-primary/10 text-primary border-primary/20' },
  vendorshield_company_manager: { label: 'VL Manager', icon: Users, color: 'bg-primary/10 text-primary border-primary/20' },
  vendorshield_coordinator: { label: 'VL Coord', icon: Users, color: 'bg-primary/10 text-primary border-primary/20' },
};

const SidebarRoleIndicator: React.FC<SidebarRoleIndicatorProps> = ({ role, collapsed }) => {
  const config = roleConfig[role] || { label: 'User', icon: Users, color: 'bg-muted text-muted-foreground' };
  const Icon = config.icon;

  if (collapsed) {
    return (
      <div className="flex justify-center py-2">
        <div className={`p-2 rounded-lg ${config.color}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <Badge 
        variant="outline" 
        className={`w-full justify-start gap-2 py-1.5 ${config.color}`}
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="text-xs font-medium">{config.label}</span>
      </Badge>
    </div>
  );
};

export default SidebarRoleIndicator;
